import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowLeftOutline, ArrowRightOutline, Box, Button, HStack } from '@bonusly/design-system';

interface UserSpotlightNavigationProps {
	currentIndex: number;
	totalCards: number;
	onPrevious: () => void;
	onNext: () => void;
	onGoToCard: (index: number) => void;
}

export const UserSpotlightNavigation: FC<UserSpotlightNavigationProps> = ({ currentIndex, totalCards, onPrevious, onNext, onGoToCard }) => {
	const { t } = useTranslation('spotlight');
	const isFirst = currentIndex === 0;
	const isLast = currentIndex === totalCards - 1;

	return (
		<Box
			position={{ base: 'fixed', md: 'relative' }}
			bottom={{ base: 0, md: 'auto' }}
			left={0}
			right={0}
			width="100%"
			maxW={{ base: '100%', md: '900px' }}
			mx="auto"
			mt={{ base: 0, md: 8 }}
			mb={{ base: 0, md: 0 }}
			pb={{ base: 4, md: 0 }}
			pt={{ base: 4, md: 0 }}
			bg="transparent"
			backdropFilter={{ base: 'blur(10px)', md: 'none' }}
			display="flex"
			justifyContent="space-between"
			alignItems="center"
			zIndex={50}
			sx={{
				'@media (max-height: 840px)': {
					position: 'fixed',
					bottom: '32px',
				},
			}}
		>
			{/* Mobile: Just buttons with equal padding */}
			<Box display={{ base: 'flex', md: 'none' }} width="100%" justifyContent="space-between" px={4}>
				<Button
					leftIcon={<ArrowLeftOutline size="sm" />}
					variant="ghost"
					onClick={onPrevious}
					disabled={isFirst}
					px={4}
					py={2}
					color="text.soft"
					fontSize={{ base: 'xs', md: 'sm' }}
					_hover={{
						bg: 'none',
					}}
					_active={{
						bg: 'none',
					}}
				>
					{t('navigation.back')}
				</Button>

				<Button
					rightIcon={<ArrowRightOutline size="sm" />}
					variant="solid"
					onClick={onNext}
					visibility={isLast ? 'hidden' : 'visible'}
					bg="secondary.bg"
					color="text.onColor"
					px={4}
					py={2}
					h="auto"
					fontSize={{ base: 'xs', md: 'sm' }}
					_hover={{
						bg: 'secondary.bgHover',
						color: 'text.onColor',
					}}
					_active={{
						bg: 'secondary.bgActive',
						color: 'text.onColor',
					}}
				>
					{t('navigation.next')}
				</Button>
			</Box>

			{/* Desktop: Buttons with pills in middle */}
			<Box display={{ base: 'none', md: 'flex' }} width="100%" justifyContent="space-between" alignItems="center">
				<Button
					leftIcon={<ArrowLeftOutline size="sm" />}
					variant="ghost"
					onClick={onPrevious}
					disabled={isFirst}
					px="20px"
					py="10px"
					color="text.soft"
					_hover={{
						bg: 'none',
					}}
					_active={{
						bg: 'none',
					}}
				>
					{t('navigation.back')}
				</Button>

				<HStack spacing={2}>
					{Array.from({ length: totalCards }).map((_, index) => (
						<Box
							key={index}
							as="button"
							aria-label={t('navigation.goToCardAria', { number: index + 1 })}
							aria-current={index === currentIndex ? 'step' : undefined}
							w={index === currentIndex ? '40px' : '14px'}
							h="14px"
							borderRadius="20px"
							bg={index === currentIndex ? 'text.default' : 'bg.surface'}
							border="1px solid"
							borderColor={index === currentIndex ? 'transparent' : 'border.default'}
							boxShadow={index === currentIndex ? 'sm' : 'xs'}
							cursor="pointer"
							transition="all 0.2s ease"
							onClick={() => onGoToCard(index)}
							_hover={{
								bg: index === currentIndex ? 'text.default' : 'bg.surface',
								boxShadow: index === currentIndex ? 'sm' : 'sm',
							}}
						/>
					))}
				</HStack>

				<Button
					rightIcon={<ArrowRightOutline size="sm" />}
					variant="solid"
					onClick={onNext}
					visibility={isLast ? 'hidden' : 'visible'}
					bg="secondary.bg"
					color="text.onColor"
					px="20px"
					py="10px"
					h="auto"
					fontSize="sm"
					_hover={{
						bg: 'secondary.bgHover',
						color: 'text.onColor',
					}}
					_active={{
						bg: 'secondary.bgActive',
						color: 'text.onColor',
					}}
				>
					{t('navigation.next')}
				</Button>
			</Box>
		</Box>
	);
};
